<template>
  <v-container fluid pa-0>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-container fluid py-0 pb-1>
          <v-row align="center">
            <div>
              <h1 class="headline">Templates</h1>
            </div>
            <v-spacer></v-spacer>
            <v-col class="py-0">
              <list-filters
                :filters="filters"
                label="Find Template"
                searchButtonId="findTemplateBtn"
                ref-name="templatesListFilters"
                @searchParamsChanged="searchParamsChanged($event)"
                :expansion-panel="false"
              ></list-filters>
            </v-col>
            <div>
              <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
            </div>
            <v-select
              v-if="showGroupedView"
              :items="groupByOptions"
              v-model="selectedGroupedBy"
              label="Group by"
              item-text="name"
              return-object
              :outlined="outlinedInputs"
              hide-details
              class="mx-1"
              dense
            ></v-select>
            <v-switch
              prepend-icon="mdi-view-list"
              class="mr-3"
              label="Group View"
              v-model="showGroupedView"
            ></v-switch>
            <div>
              <list-visible-columns-and-filter
                :visible-columns="visibleColumns"
                :selectable-headers="selectableHeaders"
                v-model="search"
                @change="onVisibleColumnsChange"
                :ref-name="refNameColumns"
              ></list-visible-columns-and-filter>
            </div>
            <div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    fab
                    dark
                    color="primary"
                    @click="onCreateNew"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Create New Template</span>
              </v-tooltip>
            </div>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-row v-if="showGroupedView">
      <v-col class="py-0">
        <list-grouped :group="selectedGroupedBy" :items="items">
          <template v-slot:table="{ items }">
            <v-card>
              <templates-table
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                @deleteItem="deleteItem"
                @editItem="editItem"
                @editItemContents="editItemContents"
                @showTemplateSettings="onShowTemplateSettings"
                @cloneItem="cloneItem"
              ></templates-table>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
    </v-row>

    <v-layout fluid v-else>
      <v-flex xs12>
        <div v-if="showAgGrid" key="ag-grid">
          <ag-grid-vue
            class="ag-theme-alpine"
            id="ag-template-list"
            :columnDefs="agGridColumns"
            :rowData="items"
            rowSelection="multiple"
            :context="agContext"
            @grid-ready="onAgGridReady"
            @columnMoved="onAgColumnMoved"
            @columnResized="onAgColumnResized"
            @sortChanged="onAgSortChanged"
            @selection-changed="onAgSelectionChanged"
            @newColumnsLoaded="onAgNewColumns"
            suppressDragLeaveHidesColumns
          >
          </ag-grid-vue>
        </div>
        <v-data-table
          v-else
          :headers="filteredHeaders"
          :items="items"
          :loading="$apollo.loading"
          v-bind="pagingOptions"
          :search="search"
          item-key="id"
          class="elevation-1"
          fixed-header
          hide-default-footer
          @update:options="onListOptionsUpdate"
          :height="gridHeight"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
        >
          <template v-slot:item.duration="{ item }">
            {{
              `${item.duration} days/${Number(item.duration) - 1} Nights`
            }}</template
          >
          <template v-slot:item.description="{ item }">{{
            item && item.description && item.description.length > 50
              ? `${item.description.slice(0, 50)}...`
              : item.description
          }}</template>
          <template v-slot:item.marketPlace="{ item }">
            <v-simple-checkbox
              v-model="item.marketPlace"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <template-list-actions
              :item="item"
              @deleteItem="deleteItem"
              @editItem="editItem"
              @editItemContents="editItemContents"
              @showTemplateSettings="onShowTemplateSettings"
              @cloneItem="cloneItem"
            />
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
    <template-settings-dialog
      :show="showTemplateSettings"
      :selected-template="selectedTemplate"
      @createItinerary="onCreateItinerary"
      @onCancel="showTemplateSettings = false"
      create-itinerary
    ></template-settings-dialog>
    <v-dialog
      v-model="templateContentsDialog"
      min-width="1200"
      persistent
      :key="selectedTemplate ? selectedTemplate.id : 'templateContentsDialog'"
      scrollable
      v-if="selectedTemplate"
    >
      <v-card>
        <v-card-title class="grey lighten-3">
          <v-row>
            <v-icon class="draggable">mdi-drag</v-icon>
            <v-col class="py-0">{{ selectedTemplate.name }} - Contents</v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <contents
            v-if="selectedTemplate && selectedTemplate.id != null"
            :owner-id="selectedTemplate.id"
            owner-name="TEMPLATE"
          ></contents>
        </v-card-text>
        <v-card-actions class="grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn color="warning" text @click="templateContentsDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <copy-template-dialog
      v-model="selectedTemplate"
      :show="showCopyTemplateDialog"
      @close="showCopyTemplateDialog = false"
      @copy="onCopyTemplate"
    />
    <v-row v-if="debug">
      <v-col>
        <pre>{{ closeoutsMsg }}</pre>
      </v-col>
    </v-row>
    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="templates"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('templates')"
    ></get-more-results>
  </v-container>
</template>

<script>
import { ListMixin } from "../../../mixins/ListMixin";
import AgGridMixin from "../../../mixins/AgGridListMixin";

import GetMoreResults from "../shared/GetMoreResults";
import DefaultListActions from "../shared/DefaultListActions";
import Contents from "../shared/contents/Contents";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import TemplateSettingsDialog from "./TemplateSettingsDialog";
import ListFilters from "../shared/list-filters/ListFilters";
import templateFilters from "./templates-filters";
import gql from "graphql-tag";
import { getItiItemOrder, removeGQLTypename } from "../../../helpers";
import CopyTemplateDialog from "./CopyTemplateDialog.vue";
import AgGridSwitch from "../shared/AgGridSwitch.vue";
import { AgGridVue } from "ag-grid-vue";
import TemplateListActions from "./TemplateListActions.vue";
import ListGrouped from "../shared/ListGrouped.vue";
import TemplatesTable from "./TemplatesTable.vue";
import moment from "moment";
const CREATE_ITINERARY = gql`
  mutation templateCreateItinerary($input: ItineraryInput!) {
    createItinerary(itineraryInput: $input) {
      id
      reference
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteTemplate($id: ID!) {
    deleteTemplate(id: $id) {
      id
    }
  }
`;
const TEMPLATES_LIST = gql`
  query templatesList(
    $after: String
    $filter: TemplateFilterType
    $sort: SortOrderType
  ) {
    templates(first: 50, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          description
          brand
          internalStatus
          marketPlace
          destination {
            id
            name
          }
          templateType {
            id
            name
          }
          department
          duration
        }
      }
    }
  }
`;
const CLONE_TEMPLATE = gql`
  mutation cloneTemplate($input: CloneTemplateInput!) {
    cloneTemplate(input: $input) {
      id
    }
  }
`;
const CHECK_FOR_CLOSEOUTS = gql`
  query checkForCloseouts($input: CheckCloseoutInput!) {
    checkForCloseouts(input: $input) {
      supplierName
      serviceName
      hasCloseouts
      closeoutDates
    }
  }
`;
export default {
  name: "TemplateList",

  props: {
    refName: {
      type: String,
      default: () => "templates_list",
    },
    refNameColumns: {
      type: String,
      default: () => "templates_list_columns",
    },
  },

  mixins: [ListMixin, AgGridMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListVisibleColumnsAndFilter,
    TemplateSettingsDialog,
    ListFilters,
    Contents,
    CopyTemplateDialog,
    AgGridSwitch,
    AgGridVue,
    TemplateListActions,
    ListGrouped,
    TemplatesTable,
  },

  data() {
    return {
      debug: false,
      templates: {},
      search: "",
      showGroupedView: false,
      selectedGroupedBy: null,
      selectedGroupedBy: {
        name: this.$store.getters.isCPS ? "Itinerary Destination" : "Brand",
        getKey: (item) => item.brand,
        getLabel: (item) => `${this.$store.getters.isCPS ? "Itinerary Destination" : "Brand"}: ${item.brand}`,
      },
      groupByOptions: [
        {
          name: this.$store.getters.isCPS ? "Itinerary Destination" : "Brand",
          getKey: (item) => item.brand,
          getLabel: (item) => `${this.$store.getters.isCPS ? "Itinerary Destination" : "Brand"}: ${item.brand}`,
        },
        {
          name: "Department",
          getKey: (item) => item.department,
          getLabel: (item) => `Department: ${item.department}`,
        },
        {
          name: "Template Type",
          getKey: (item) => (item.templateType ? item.templateType.name : ""),
          getLabel: (item) =>
            `Template Type: ${item.templateType ? item.templateType.name : ""}`,
        },
        {
          name: "Destination",
          getKey: (item) => (item.destination ? item.destination.name : ""),
          getLabel: (item) =>
            `Destination: ${item.destination ? item.destination.name : ""}`,
        },
      ],
      templateFilters: templateFilters,
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "name",
        "destination",
        "department",
        "duration",
        "actions",
      ]),
      headers: [
        {
          text: "Name",

          value: "name",
          name: "name",
        },
        {
          text: "Destination",
          value: "destination.name",
          name: "destination",
          valueGetter: (payload) =>
            payload.data.destination ? payload.data.destination.name : "",
        },
        {
          text: "Department",
          value: "department",
          name: "department",
        },
        {
          text: "Duration",
          value: "duration",
          name: "duration",
        },
        {
          text: "Description",
          value: "description",
          name: "description",
        },
        {
          text: this.$store.getters.isCPS ? "Itinerary Destination" : "Brand",
          value: "brand",
          name: "brand",
        },
        {
          text: "Internal Status",
          value: "internalStatus",
          name: "internalStatus",
        },
        {
          text: "Market Place",
          value: "marketPlace",
          name: "marketPlace",
        },
        {
          text: "Template Type",
          value: "templateType.name",
          name: "templateType",
          valueGetter: (payload) =>
            payload.data.templateType ? payload.data.templateType.name : "",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
          cellRendererFramework: "TemplateListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove: false,
              hideEdit: false,
            },
          }),
        },
      ],
      showTemplateSettings: false,
      selectedTemplate: null,
      itiInput: null,
      templateContentsDialog: false,
      showCopyTemplateDialog: false,
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    items() {
      return this.templates && this.templates.edges
        ? this.templates.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return templateFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        if (this.$store.getters.isCPS) {
          if (f.props.filterName == "brands_eq") {
            f.props.label = "Itinerary Destination";
          }
        }
        return f;
      });
    },
    closeoutsMsg() {
      const msg = [];
      if (
        this.checkForCloseouts &&
        this.checkForCloseouts.length > 0 &&
        this.checkForCloseouts.some((c) => c.hasCloseouts)
      ) {
        msg.push("Closeouts found for the following services:");
        for (const closeout of this.checkForCloseouts) {
          if (closeout.hasCloseouts) {
            msg.push(
              `${closeout.supplierName} - ${
                closeout.serviceName
              } - ${closeout.closeoutDates.join(", ")}`
            );
          }
        }
      }
      return msg.join("\n");
    },
  },

  methods: {
    onCopyTemplate(payload) {
      //console.log("onCopyTemplate", payload);
      this.$apollo
        .mutate({
          mutation: CLONE_TEMPLATE,
          variables: {
            input: {
              ...payload,
            },
          },
          refetchQueries: [
            {
              query: TEMPLATES_LIST,
              fetchPolicy: "network-only",
              variables: {
                after: this.after,
                filter: {
                  ...this.searchParams,
                  templateType_eq: "BASIC",
                },
                sort: this.sort,
              },
            },
          ],
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Template Copied.",
          });
          this.showCopyTemplateDialog = false;
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors
            .map((err) => eerr.message)
            .join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    cloneItem(item) {
      this.selectedTemplate = item;
      this.showCopyTemplateDialog = true;
    },
    editItemContents(item) {
      this.selectedTemplate = item;
      this.templateContentsDialog = true;
    },
    editItem(item) {
      this.$router.push(`/template/${item.id}/edit`);
    },
    onCreateNew() {
      this.$router.push("/template/new");
    },
    onShowTemplateSettings(item) {
      this.selectedTemplate = { ...item };

      this.showTemplateSettings = true;
    },
    onCreateItinerary(payload, priceCheck = true) {
      this.itiInput = { ...payload };
      //add template name #http://tracker.globetrack.ie/issues/5802
      payload.internalReference = this.selectedTemplate.name;
      //update items order
      const added = [];
      for (let item of payload.items) {
        item.orderNo = getItiItemOrder(payload)(added)(item);
        added.push(item);
      }
      this.$apollo
        .mutate({
          mutation: CREATE_ITINERARY,
          // #11320 — pass the template id so the server can copy the template's content
          // onto the new itinerary (gated on the "Template Copy Content" access setting).
          variables: {
            input: {
              ...payload,
              priceCheck,
              copyContentFromTemplateId: this.selectedTemplate.id,
            },
          },
        })
        .then(({ data }) => {
          this.showTemplateSettings = false;
          this.$store.dispatch("showAppSnackbar", {
            msg: "Itinerary Created.",
          });
          this.$router.push(`/itinerary/${data.createItinerary.id}/details`);
        })
        .catch((err) => {
          // this.$store.dispatch("showAppSnackbar", {
          //   msg: err,
          //   color: "error",
          // });
          const errorMsg =
            "Some services have NO VALID Prices for your selected dates. These services will add to the itinerary with a zero price. By continuing, you undestand you MUST check your service prices.";
          if (window.confirm(`${errorMsg}\n Do you wish to continue?`)) {
            this.onCreateItinerary(this.itiInput, false);
          }
          console.error(err);
        });
    },

    async deleteItem(payload) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: {
            id: payload.id,
          },
          refetchQueries: [
            {
              query: TEMPLATES_LIST,
              fetchPolicy: "network-only",
              variables: {
                after: this.after,
                filter: {
                  ...this.searchParams,
                  templateType_eq: "BASIC",
                },
                sort: this.sort,
              },
            },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Template deleted",
        });
      } catch (error) {
        console.log(error);
      }
    },
  },

  apollo: {
    templates: {
      query: TEMPLATES_LIST,
      variables() {
        return {
          after: this.after,
          filter: {
            ...this.searchParams,
            templateType_eq: "BASIC",
          },
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.templates.totalCount;
        //  this.pagination.totalItems = data.itineraries.totalCount;
      },
    },
    checkForCloseouts: {
      query: CHECK_FOR_CLOSEOUTS,
      variables() {
        return {
          input: {
            items: this.itiInput.items,
            agentId: this.itiInput.agentId,
            bookingDate: moment().format("YYYY-MM-DD"),
          },
        };
      },
      skip() {
        return !this.itiInput;
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        if (this.closeoutsMsg.trim().length > 0) {
          alert(this.closeoutsMsg);
        }
      },
    },
  },
};
</script>

<style></style>
<style scoped>
#ag-template-list {
  height: calc(100vh - 230px);
}
</style>


// WEBPACK FOOTER //
// src/components/gt/templates/TemplateList.vue